import Grid from '@mui/material/Grid2';

import { SelectField, TextInputField } from '@/components/forms';
import { meetingTypes } from '@/schemas/enquiry';

import { type ConsultationFormState } from './useConsultationForm';

/**
 * The import-advice request's five questions, and nothing else.
 *
 * There are no `FieldGroup` legends any more. Two fieldsets — "About you" and
 * "Your meeting" — were doing real work across eight fields; across five they
 * are two headings introducing two and three questions, which is more chrome
 * than content and makes a short form look like a long one. The card's own h2
 * names the form, and five labelled inputs do not need a grouping on top of
 * it.
 *
 * Name and number sit on one row from `sm`, and everything below runs full
 * width. A select and a message box gain nothing from being half a card wide,
 * and the single column pulls the eye straight down to the submit.
 *
 * Email carries no "(optional)" because it is not: it is the one form on the
 * site that requires it, since the advice itself arrives in writing. The
 * helper text says what it is for rather than making the visitor guess why
 * this form asks for more than the last one did.
 *
 * This component holds no state and reaches for no config beyond the choices
 * the schema already owns.
 */
type Props = Pick<ConsultationFormState, 'control'>;

export function ConsultationFields({ control }: Props) {
  return (
    <Grid container spacing={5}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextInputField
          control={control}
          name="fullName"
          label="Full name"
          required
          autoComplete="name"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextInputField
          control={control}
          name="phone"
          label="WhatsApp number"
          required
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+256 700 000 000"
          helperText="Include the country code."
        />
      </Grid>
      <Grid size={12}>
        <TextInputField
          control={control}
          name="email"
          label="Email"
          required
          type="email"
          autoComplete="email"
          helperText="We send the written summary here."
        />
      </Grid>
      <Grid size={12}>
        <SelectField
          control={control}
          name="meetingType"
          label="How should we reach you?"
          emptyLabel="Choose one"
          options={meetingTypes}
        />
      </Grid>
      <Grid size={12}>
        <TextInputField
          control={control}
          name="message"
          label="What would you like to ask? (optional)"
          multiline
          rows={4}
          helperText="A model, a shortlist, or just a budget and what you need the car to do."
        />
      </Grid>
    </Grid>
  );
}
